import * as React from 'react';
import { useState } from 'react';

import { X } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { formatStopsDisplay } from 'pages/QuoteView/Quoting/RequestQuickQuote/utils/location';
import { Stop } from 'types/QuoteRequest';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import {
  QuoteChanges,
  SuggestedQuoteChange,
} from 'types/suggestions/QuoteSuggestions';
import { cn } from 'utils/shadcn';

import { formatDrumkitQuoteNumber, parseDrumkitQuoteNumber } from './utils';

export type QuoteNumberOption = {
  id: number;
  drumkitQuoteNumber: string;
  pickupCity?: string;
  pickupState?: string;
  deliveryCity?: string;
  deliveryState?: string;
  stops?: Stop[];
  pickupDate?: string | Date | null;
  deliveryDate?: string | Date | null;
  lowBuyRate?: number;
  highBuyRate?: number;
  targetBuyRate?: number;
};

type QuoteNumberSelectProps = {
  value: string;
  options: QuoteNumberOption[];
  isLoading?: boolean;
  showAIHint?: boolean;
  onChange: (next: {
    drumkitQuoteNumber: string;
    option?: QuoteNumberOption;
  }) => void;
  className?: string;
};

/** City, ST for a single lane endpoint (empty parts omitted). */
function formatEndpoint(city?: string, state?: string): string {
  return [city?.trim(), state?.trim()].filter(Boolean).join(', ');
}

/**
 * Lane label for a quote-number option.
 * Uses QQ multi-stop arrow notation when ordered stops are present
 * (e.g. "Boston, MA → Hartford, CT → New York, NY"); otherwise
 * pickup → delivery (or a single side / "Lane unavailable").
 */
export function formatLane(option: QuoteNumberOption): string {
  const stopCount = option.stops?.length ?? 0;
  const stopLabels = formatStopsDisplay(
    (option.stops ?? []).map((stop) => ({
      city: stop.address?.city,
      state: stop.address?.state,
      zip: stop.address?.zip,
    }))
  );

  // Multi-stop / full route from QQ stops (same arrow notation as QQ UI).
  if (stopCount >= 2 && stopLabels) {
    return stopLabels;
  }

  const origin = formatEndpoint(option.pickupCity, option.pickupState);
  const dest = formatEndpoint(option.deliveryCity, option.deliveryState);
  if (origin && dest) {
    return `${origin} → ${dest}`;
  }
  return origin || dest || stopLabels || 'Lane unavailable';
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) {
    return '';
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatMoney(value: number | undefined): string {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return '—';
  }
  return `$${value.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Prefer applied (lane the user actually quoted) over suggested (AI/portal extract).
 * Thread QQ searches often have both; load-builder should show what was submitted.
 */
export function resolveQuoteLaneSource(
  quote: SuggestedQuoteChange
): Partial<QuoteChanges> | undefined {
  if (quoteLaneHasDisplayableRoute(quote.applied)) {
    return quote.applied;
  }
  return quote.suggested;
}

/** True when route has enough city/state/stops to render a lane label. */
export function quoteLaneHasDisplayableRoute(
  route: Partial<QuoteChanges> | undefined
): boolean {
  if (!route) {
    return false;
  }
  if ((route.stops?.length ?? 0) >= 2) {
    return route.stops!.some(
      (s) =>
        Boolean(s.address?.city?.trim()) ||
        Boolean(s.address?.state?.trim()) ||
        Boolean(s.address?.zip?.trim())
    );
  }
  return Boolean(
    route.pickupCity?.trim() ||
    route.deliveryCity?.trim() ||
    route.pickupState?.trim() ||
    route.deliveryState?.trim()
  );
}

export function mapSuggestedQuoteToOption(
  quote: SuggestedQuoteChange
): QuoteNumberOption {
  const lane = resolveQuoteLaneSource(quote);
  return {
    id: quote.id,
    drumkitQuoteNumber:
      quote.drumkitQuoteNumber?.trim() || formatDrumkitQuoteNumber(quote.id),
    pickupCity: lane?.pickupCity,
    pickupState: lane?.pickupState,
    deliveryCity: lane?.deliveryCity,
    deliveryState: lane?.deliveryState,
    stops: lane?.stops,
    pickupDate: lane?.pickupDate,
    deliveryDate: lane?.deliveryDate,
    lowBuyRate: quote.lowBuyRate,
    highBuyRate: quote.highBuyRate,
    targetBuyRate: quote.targetBuyRate,
  };
}

function QuoteNumberOptionRow({ option }: { option: QuoteNumberOption }) {
  const pickup = formatDate(option.pickupDate);
  const delivery = formatDate(option.deliveryDate);
  const dates =
    pickup && delivery ? `${pickup} – ${delivery}` : pickup || delivery;

  return (
    <Flex direction='col' gap='xs' className='w-full py-0.5'>
      <Flex align='center' justify='between' gap='sm' className='w-full'>
        <Typography variant='body-sm' weight='semibold' className='text-[13px]'>
          {option.drumkitQuoteNumber}
        </Typography>
        <Typography variant='body-xs' className='text-neutral-600 shrink-0'>
          Target {formatMoney(option.targetBuyRate)}
        </Typography>
      </Flex>
      <Typography variant='body-xs' className='text-neutral-700'>
        {formatLane(option)}
      </Typography>
      <Flex align='center' justify='between' gap='sm' className='w-full'>
        {dates ? (
          <Typography variant='body-xs' className='text-neutral-500'>
            {dates}
          </Typography>
        ) : null}
        <Typography variant='body-xs' className='text-neutral-500 shrink-0'>
          Min {formatMoney(option.lowBuyRate)} · Max{' '}
          {formatMoney(option.highBuyRate)}
        </Typography>
      </Flex>
    </Flex>
  );
}

/**
 * Create-mode Drumkit Quote Number picker (ENG-7570).
 * Options are thread QQ searches; clear is optional.
 */
export default function QuoteNumberSelect({
  value,
  options,
  isLoading = false,
  showAIHint = false,
  onChange,
  className,
}: QuoteNumberSelectProps) {
  const selectedId = parseDrumkitQuoteNumber(value);
  const selectedValue = selectedId > 0 ? String(selectedId) : undefined;
  const [selectEpoch, setSelectEpoch] = useState(0);

  const selectedInOptions = options.some((o) => o.id === selectedId);
  const displayOptions =
    selectedId > 0 && !selectedInOptions
      ? [
          {
            id: selectedId,
            drumkitQuoteNumber:
              value.trim() || formatDrumkitQuoteNumber(selectedId),
          } satisfies QuoteNumberOption,
          ...options,
        ]
      : options;

  const clearSelection = () => {
    setSelectEpoch((epoch) => epoch + 1);
    onChange({ drumkitQuoteNumber: '' });
  };

  return (
    <Flex align='start' gap='xs' className={cn('w-full', className)}>
      <div className='min-w-0 flex-1'>
        <Select
          key={selectEpoch}
          value={selectedValue}
          onValueChange={(idStr) => {
            const id = Number(idStr);
            if (!Number.isFinite(id) || id <= 0) {
              return;
            }
            const option = options.find((o) => o.id === id);
            onChange({
              drumkitQuoteNumber:
                option?.drumkitQuoteNumber || formatDrumkitQuoteNumber(id),
              option,
            });
          }}
          disabled={isLoading && options.length === 0 && !selectedValue}
        >
          <SelectTrigger
            className='mt-1 h-auto min-h-9 py-1.5 text-[13px]'
            aria-label='Drumkit Quote Number'
          >
            <SelectValue placeholder='Select quote number (optional)' />
          </SelectTrigger>
          <SelectContent className='max-w-[min(100vw,22rem)]'>
            {displayOptions.map((option) => (
              <SelectItem
                key={option.id}
                value={String(option.id)}
                textValue={option.drumkitQuoteNumber}
                className='items-start py-2'
              >
                <QuoteNumberOptionRow option={option} />
              </SelectItem>
            ))}
            {options.length === 0 && (
              <div className='px-2 py-3'>
                <Typography variant='body-xs' textColor='muted'>
                  {isLoading
                    ? 'Loading quote searches…'
                    : 'No Quick Quote searches on this thread.'}
                </Typography>
              </div>
            )}
          </SelectContent>
        </Select>
        {showAIHint && value && (
          <Typography variant='body-xs' className='mt-1 text-accent-600'>
            AI-filled — confirm or change if you quoted multiple lanes
          </Typography>
        )}
      </div>
      {value ? (
        <Button
          type='button'
          variant='ghost'
          size='xs'
          buttonNamePosthog={ButtonNamePosthog.MGBQuoteNumberCleared}
          className='mt-1 shrink-0'
          aria-label='Clear quote number'
          onClick={clearSelection}
        >
          <X className='h-4 w-4' />
        </Button>
      ) : null}
    </Flex>
  );
}
